import React, { useEffect, useState, useCallback } from 'react';
import { Form, Button, Row, Col, Container, Spinner } from 'react-bootstrap';
import { Editor } from '@tinymce/tinymce-react';
import ImageUpload from './ImageUpload';
import { toast } from 'react-toastify';
import { getError } from '../../Utils';
import Axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const EditArticle = () => {
  const appBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { id } = useParams(); // Get article ID from URL
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    author: '',
    category: '',
    image: null,
    imageUrl: '',
    description: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
  });
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articleLoading, setArticleLoading] = useState(true); // State for article loading
  const [articleSlug, setArticleSlug] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      image: e.target.files[0],
    });
  };

  const setImageUrl = (url) => {
    setFormData({
      ...formData,
      imageUrl: url,
    });
  };

  const handleEditorChange = (content) => {
    setFormData({
      ...formData,
      description: content,
    });
  };

  const transformFormData = (formData) => {
    return {
      name: formData.title,
      slug: formData.slug,
      author: formData.author,
      category: formData.category,
      imageUrl: formData.imageUrl,
      description: formData.description,
      metaTitle: formData.metaTitle,
      metaDescription: formData.metaDescription,
      metaKeywords: formData.metaKeywords,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Handle form submission
    const transformedData = transformFormData(formData);

    try {
      const { data } = await Axios.put(
        `${appBaseUrl}/api/articles/${id}`, // Use PUT for updates
        transformedData
      );
      if (data._id) {
        toast.success('Success! Article updated successfully.');
        navigate('/admin/articles'); // Navigate back to article listing or any other page
      }
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const { data } = await Axios.get(`${appBaseUrl}/api/categories`);
      setCategories(data);
      const authorData = await Axios.get(`${appBaseUrl}/api/users/authors`);
      setAuthors(authorData?.data);
      setLoading(false);
    } catch (err) {
      toast.error(getError(err));
      setLoading(false);
    }
  }, [appBaseUrl]);

  const fetchArticle = useCallback(
    async (id) => {
      setArticleLoading(true);
      try {
        const { data } = await Axios.get(
          `${appBaseUrl}/api/articles/article/${id}`
        );

        setFormData({
          title: data.name,
          author: data.author._id,
          category: data.category._id,
          imageUrl: data.imageUrl,
          description: data.description,
          metaTitle: data.metaTitle,
          metaDescription: data.metaDescription,
          metaKeywords: data.metaKeywords,
        });
        setArticleSlug(data?.slug);
        setArticleLoading(false);
      } catch (err) {
        toast.error(getError(err));
        setArticleLoading(false);
      }
    },
    [appBaseUrl]
  );

  useEffect(() => {
    fetchData();
    fetchArticle(id); // Fetch article data
  }, [id, fetchData, fetchArticle]);

  if (articleLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  const handleImageUpload = async (blobInfo, success, failure) => {
    const formData = new FormData();
    formData.append('file', blobInfo.blob(), blobInfo.filename());
    formData.append('upload_preset', 'wcpnuesk');

    const response = await Axios.post(
      'https://api.cloudinary.com/v1_1/daqe8jjzi/image/upload',
      formData
    );
    // Extract image URL
    const editorImageUrl = response?.data?.secure_url ?? '';
    return new Promise((resolve) => {
      // Simulate successful upload
      setTimeout(() => {
        resolve(editorImageUrl);
      }, 1000); // Simulate a delay of 1 second
    });
  };

  return (
    <Container id="content" fluid>
      <h1>Edit Article</h1>
      <Form onSubmit={handleSubmit}>
        {!articleSlug && (
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="formTitle">
                <Form.Label>Article Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter article title"
                />
              </Form.Group>
            </Col>
          </Row>
        )}
        <Row className="mb-3">
          {articleSlug ? (
            <Col md={6}>
              <Form.Group controlId="formTitle">
                <Form.Label>Article Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter article title"
                />
              </Form.Group>
            </Col>
          ) : (
            <Col md={6}>
              <Form.Group controlId="formSlug">
                <Form.Label>Article Slug</Form.Label>
                <Form.Control
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="Enter article slug"
                />
              </Form.Group>
            </Col>
          )}
          <Col md={6}>
            <Form.Group controlId="formAuthor">
              <Form.Label>Author</Form.Label>
              <Form.Select
                name="author"
                value={formData.author}
                onChange={handleChange}
              >
                <option value="">Select author</option>
                {authors?.map((author) => {
                  return (
                    <option key={author._id} value={author._id}>
                      {author.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formCategory">
              <Form.Label>Article Category</Form.Label>
              {loading ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          </Col>
          <Col md={1}>
            {formData.imageUrl && (
              <Row className="mb-3">
                <Col>
                  <img
                    src={formData.imageUrl}
                    alt="Article"
                    style={{ width: '100%' }}
                  />
                </Col>
              </Row>
            )}
          </Col>
          <Col md={5}>
            <ImageUpload
              handleFileChange={handleFileChange}
              setImageUrl={setImageUrl}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="formMetaTitle">
              <Form.Label>Meta Title</Form.Label>
              <Form.Control
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                placeholder="Enter meta title"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formMetaKeywords">
              <Form.Label>Meta Keywords (Comma Separated)</Form.Label>
              <Form.Control
                type="text"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleChange}
                placeholder="e.g., technology, news, article"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={12}>
            <Form.Group controlId="formMetaDescription">
              <Form.Label>Meta Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                placeholder="Enter meta description"
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group controlId="formDescription" className="mb-3">
          <Form.Label>Enter Article Description</Form.Label>
          <Editor
            apiKey="2ao4h0qyfjkzmrv1mwfwut2c9dsnnhuveoxvb5zsivnbdttt" // Optional: Your TinyMCE API key
            value={formData.description}
            init={{
              selector: 'textarea#basic-example',
              height: 500,
              plugins: [
                'advlist',
                'autolink',
                'lists',
                'link',
                'image',
                'charmap',
                'preview',
                'anchor',
                'searchreplace',
                'visualblocks',
                'code',
                'fullscreen',
                'insertdatetime',
                'media',
                'table',
                'help',
                'wordcount',
                'pageembed',
              ],
              toolbar:
                'undo redo | image | blocks | ' +
                'bold italic backcolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | pageembed | help',
              images_upload_handler: handleImageUpload,
              content_style:
                'body { font-family:Helvetica,Arial,sans-serif; font-size:16px }',
            }}
            onEditorChange={handleEditorChange}
          />
        </Form.Group>

        <Button
          variant="primary"
          type="submit"
          disabled={!formData.imageUrl} // Disable button if image URL is not set
        >
          Update
        </Button>
      </Form>
    </Container>
  );
};

export default EditArticle;
